import {
  AbsoluteFloorPolylineSegmentGeometry,
  AbsoluteTopOrBottomCut,
  AbsoluteVerticalCut,
  BearingEnvelope,
  FloorPolylineSegmentGeometry,
  RoofPlaneAbsoluteGeometry,
  TopOrBottomCut,
} from '@paragon/contract-library';
import { Line2D, Polygon3D } from '@paragon/geometry-library';
import {
  calculateBearingEnvelopeBasedPlaneGeometry,
  convertPlaneToGeometricPlane,
  floorPolylineSegmentToLineSegment,
  PlaneWithCalculatedGeometry,
  RoofOrCeilingPlane,
} from '@paragon/layout';
import { assertExhaustiveSwitch, cycle, ReadonlyDictionary } from '@paragon/utilities';

export function absolutizePlaneGeometry<T extends RoofOrCeilingPlane>(
  plane: T,
  dependencies: {
    bearingEnvelopes: ReadonlyDictionary<BearingEnvelope>;
  },
): RoofPlaneAbsoluteGeometry {
  switch (plane.geometry.type) {
    case 'Absolute':
      return plane.geometry; // already absolute

    case 'BearingEnvelope': {
      const calculatedGeometry = calculateBearingEnvelopeBasedPlaneGeometry(plane, dependencies.bearingEnvelopes);
      if (calculatedGeometry == null || calculatedGeometry.segment == null) {
        throw new Error(`Cannot calculate geometry for plane ${plane.guid}`);
      }

      // calculateBearingEnvelopeBasedPlaneGeometry respects flipping, but this method should not, so we may need to un-flip
      const { basePoint, endPoint } = plane.flipped ? calculatedGeometry.segment.reverse() : calculatedGeometry.segment;

      return {
        type: 'Absolute',
        isInfinite: false,
        elevation: calculatedGeometry.elevation,
        segment: { basePoint, endPoint },
      };
    }

    default:
      assertExhaustiveSwitch(plane.geometry);
  }
}

export function absolutizeTrussEnvelopeCut(
  cut: TopOrBottomCut,
  type: 'Top' | 'Bottom',
  dependencies: {
    roofAndCeilingPlanes: ReadonlyDictionary<PlaneWithCalculatedGeometry | null>;
  },
): AbsoluteTopOrBottomCut {
  let definingPlaneGuid: string;

  switch (cut.type) {
    case 'Absolute':
      return cut; // already absolute

    case 'RoofPlane':
      definingPlaneGuid = cut.roofPlaneGuid;
      break;

    case 'CeilingPlane':
      definingPlaneGuid = cut.ceilingPlaneGuid;
      break;

    default:
      assertExhaustiveSwitch(cut);
  }

  const definingPlane = dependencies.roofAndCeilingPlanes[definingPlaneGuid];
  if (definingPlane == null) {
    throw new Error(`Cannot find plane with guid ${definingPlaneGuid} to absolutize cut.`);
  }

  const planeSide = type === 'Top' ? 'Bottom' : 'Top';
  const planeGeometry = convertPlaneToGeometricPlane(definingPlane, planeSide);

  return { type: 'Absolute', plane: planeGeometry.toObject() };
}

export function absolutizeFloorPolylineSegmentGeometry(
  section: FloorPolylineSegmentGeometry,
  dependencies: {
    bearingEnvelopes: ReadonlyDictionary<BearingEnvelope>;
  },
): AbsoluteFloorPolylineSegmentGeometry {
  switch (section.type) {
    case 'AbsoluteVertical':
      return section; // already absolute

    case 'AgainstBearing': {
      const segment = floorPolylineSegmentToLineSegment(section, dependencies.bearingEnvelopes);
      if (segment == null) {
        throw new Error(
          `Cannot calculate segment for bearing envelope with guid ${section.bearingEnvelopeGuid} to absolutize floor polyline segment.`,
        );
      }

      return {
        type: 'AbsoluteVertical',
        segment: segment.toObject(),
        isInfinite: false,
      };
    }

    default:
      assertExhaustiveSwitch(section);
  }
}

export function absolutizeRoofPlaneCuts(
  planeShape: readonly Polygon3D[] | null,
  dependencies: { baseline: Line2D },
): readonly AbsoluteVerticalCut[] {
  if (planeShape == null) return [];

  // Unimplemented cases:

  // Multiple faces
  if (planeShape.length !== 1) return [];
  const [face] = planeShape;

  // Vertical planes
  if (face.isVertical()) return [];
  const edges = face.projectTo2D()!.edges;

  // Supported cases:
  let segmentsToGenerateCutsFrom;

  const indexOfBaseline = edges.findIndex((edge) => edge.toLine().equals(dependencies.baseline));
  if (indexOfBaseline === -1) {
    // Planes that do not border their base line
    const indexOfNonParallelSegment = edges.findIndex((edge) => !edge.isParallelTo(dependencies.baseline));
    if (indexOfNonParallelSegment === -1) return [];

    const cycledEdges = cycle(edges, indexOfNonParallelSegment);
    segmentsToGenerateCutsFrom = [...cycledEdges, edges[indexOfNonParallelSegment]];
  } else {
    // Planes that do border their base line
    const cycledEdgesMinusBaseline = edges.slice(indexOfBaseline + 1).concat(edges.slice(0, indexOfBaseline));
    segmentsToGenerateCutsFrom = cycledEdgesMinusBaseline;
  }

  return segmentsToGenerateCutsFrom.map((edge): AbsoluteVerticalCut => ({
    type: 'AbsoluteVertical',
    segment: edge.toObject(),
    isInfinite: false,
  }));
}
